import { UiSchema } from '@rjsf/core';
import { JSONSchema7 } from 'json-schema';
import { omit, pick } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { IntakeForm } from 'views/pages/client-record/useIntakeForm';
import JsonFormContainer from '../JsonFormContainer';
import TableTemplate from '../templates/TableTemplate';
import TableRowRadioGroupWidget from '../widgets/TableRowRadioGroupWidget';
import Actions from './Actions';

const uiSchema: UiSchema = {
  mentalHealthHistory_1: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  mentalHealthHistory_2: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  mentalHealthHistory_3: {
    'ui:ObjectFieldTemplate': TableTemplate,
    'ui:headers': ['Difficulty', 'Yes / No'],
    mentalHealthHistory_3_1: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_2: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_3: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_4: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_5: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_6: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_7: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_8: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_9: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_10: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_11: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_12: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_13: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_14: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_15: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_16: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_17: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } },
    mentalHealthHistory_3_18: { 'ui:widget': TableRowRadioGroupWidget, 'ui:options': { inline: true } }
  },
  mentalHealthHistory_4: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  mentalHealthHistory_4_1: { 'ui:widget': 'textarea' },
  mentalHealthHistory_5: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  mentalHealthHistory_5_1: { 'ui:widget': 'textarea' },
  mentalHealthHistory_6: { 'ui:widget': 'radio', 'ui:options': { inline: true } },
  mentalHealthHistory_6_1: { 'ui:widget': 'textarea' },
  mentalHealthHistory_7: { 'ui:widget': 'radio', 'ui:options': { inline: true } }
};

interface Props {
  intakeForm: IntakeForm;
  onPrevious?: () => void;
  isSubmitting: boolean;
  onSubmit: (data: any) => void;
  handleStep?: any;
}

export default function MentalHealthHistory({ intakeForm, onPrevious, isSubmitting, onSubmit, handleStep }: Props) {
  const intl = useIntl();

  const schema: JSONSchema7 = {
    type: 'object',
    title: intl.formatMessage({ defaultMessage: 'Mental Health History' }),
    properties: {
      mentalHealthHistory_1: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Have you had suicidal thoughts recently (past month)?' }),
        enum: [
          intl.formatMessage({ defaultMessage: 'Frequently' }),
          intl.formatMessage({ defaultMessage: 'Sometimes' }),
          intl.formatMessage({ defaultMessage: 'Rarely' }),
          intl.formatMessage({ defaultMessage: 'Never' })
        ]
      },
      mentalHealthHistory_2: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Have you had them in the past?' }),
        enum: [
          intl.formatMessage({ defaultMessage: 'Frequently' }),
          intl.formatMessage({ defaultMessage: 'Sometimes' }),
          intl.formatMessage({ defaultMessage: 'Rarely' }),
          intl.formatMessage({ defaultMessage: 'Never' })
        ]
      },
      mentalHealthHistory_3: {
        type: 'object',
        title: intl.formatMessage({ defaultMessage: 'Have you ever experienced any of the following?' }),
        properties: {
          mentalHealthHistory_3_1: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Extreme depressed mood' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })],
            default: undefined
          },
          mentalHealthHistory_3_2: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Dramatic mood swings' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })],
            default: undefined
          },
          mentalHealthHistory_3_3: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Rapid speech' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })],
            default: undefined
          },
          mentalHealthHistory_3_4: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Extreme anxiety' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })],
            default: undefined
          },
          mentalHealthHistory_3_5: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Panic attacks' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })],
            default: undefined
          },
          mentalHealthHistory_3_6: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Phobias' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_7: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Sleep disturbances' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_8: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Hallucinations' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_9: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Unexplained losses of time' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_10: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Unexplained memory lapses' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_11: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Alcohol/substance abuse' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_12: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Frequent body complaints' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_13: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Eating disorder' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_14: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Body image problems' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_15: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Repetitive thoughts (e.g. obsessions)' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_16: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Repetitive behaviors (e.g. frequent checking, hand washing)' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_17: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Homicidal thoughts' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          },
          mentalHealthHistory_3_18: {
            type: 'string',
            title: intl.formatMessage({ defaultMessage: 'Suicidal attempts' }),
            enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
          }
        }
      },
      mentalHealthHistory_4: {
        type: 'string',
        title: intl.formatMessage({
          defaultMessage: 'Trauma History: Do you have a history of being abused emotionally, sexually, physically or by neglect?'
        }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      mentalHealthHistory_4_1: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Please describe when, where and by whom:' })
      },
      mentalHealthHistory_5: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Do you regularly use alcohol?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      },
      mentalHealthHistory_5_1: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'In a typical month, how often do you have 4 or more drinks in a 24 hour period?' })
      },
      mentalHealthHistory_6: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'How often do you engage in recreational drug use?' }),
        enum: [
          intl.formatMessage({ defaultMessage: 'Daily' }),
          intl.formatMessage({ defaultMessage: 'Weekly' }),
          intl.formatMessage({ defaultMessage: 'Monthly' }),
          intl.formatMessage({ defaultMessage: 'Rarely' }),
          intl.formatMessage({ defaultMessage: 'Never' })
        ]
      },
      mentalHealthHistory_6_1: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Substances Used:' })
      },
      mentalHealthHistory_7: {
        type: 'string',
        title: intl.formatMessage({ defaultMessage: 'Do you smoke cigarettes or use other tobacco products?' }),
        enum: [intl.formatMessage({ defaultMessage: 'Yes' }), intl.formatMessage({ defaultMessage: 'No' })]
      }
    }
  };
  const [formData, setFormData] = useState({
    ...pick(intakeForm.mentalHealthHistory, [
      'mentalHealthHistory_1',
      'mentalHealthHistory_2',
      'mentalHealthHistory_4',
      'mentalHealthHistory_4_1',
      'mentalHealthHistory_5',
      'mentalHealthHistory_5_1',
      'mentalHealthHistory_6',
      'mentalHealthHistory_6_1',
      'mentalHealthHistory_7'
    ]),
    mentalHealthHistory_3: pick(intakeForm.mentalHealthHistory, [
      'mentalHealthHistory_3_1',
      'mentalHealthHistory_3_2',
      'mentalHealthHistory_3_3',
      'mentalHealthHistory_3_4',
      'mentalHealthHistory_3_5',
      'mentalHealthHistory_3_6',
      'mentalHealthHistory_3_7',
      'mentalHealthHistory_3_8',
      'mentalHealthHistory_3_9',
      'mentalHealthHistory_3_10',
      'mentalHealthHistory_3_11',
      'mentalHealthHistory_3_12',
      'mentalHealthHistory_3_13',
      'mentalHealthHistory_3_14',
      'mentalHealthHistory_3_15',
      'mentalHealthHistory_3_16',
      'mentalHealthHistory_3_17',
      'mentalHealthHistory_3_18'
    ])
  });

  return (
    <JsonFormContainer
      noValidate
      schema={schema}
      uiSchema={uiSchema}
      formData={formData}
      onChange={({ formData }) => {
        handleStep({
          mentalHealthHistory: { ...omit(formData, ['mentalHealthHistory_3']), ...formData.mentalHealthHistory_3 }
        });
        setFormData(formData);
      }}
      onSubmit={({ formData }) =>
        onSubmit({ mentalHealthHistory: { ...omit(formData, ['mentalHealthHistory_3']), ...formData.mentalHealthHistory_3 } })
      }
    >
      <Actions onPrevious={onPrevious} disabled={isSubmitting} />
    </JsonFormContainer>
  );
}
